// Shared typography + spacing for Smart Parse v3 drawer panels.

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, IconButton, Chip } from "@mui/material";
import { ChevronDown } from "lucide-react";
import ReactCountryFlag from "react-country-flag";

import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import colors from "../../../../../utils/colors";
import {
  BORDER_COLOR,
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  ERROR_COLOR,
  baseFontStyle,
  drawerSeverityPalette,
  DRAWER_SEVERITY_PALETTE,
} from "../wafEventDrawerShared";

/** Literal pixels (MUI theme spacing = 4px per unit — do not use theme units for these) */
export const SP = {
  drawer: 24,
  drawerWidth: 720,
  card: 16,
  radius: 12,
  gridCellX: 12,
  gridCellY: 10,
  assessGridGap: 8,
  kvGridGap: 7,
  chipLabel: 10,
  iconLabelGap: 11,
  gridIconTextGap: 10,
  gridLabelValueGap: 1,
};

/** Drawer-wide line-height scale — keep compact; reference uses ~1.2–1.6× font size */
export const spLh = {
  px14: "20px",
  px16: "22px",
  px18: "24px",
  px19: "25px",
  px21: "28px",
  px22: "29px",
  px24: "32px",
  px25: "34px",
  tight: 1.2,
  snug: 1.35,
  normal: 1.3,
  relaxed: 1.55,
  body: 1.6,
  copy: 1.65,
  title: 1.15,
};

export const spPanelRadius = `${SP.radius}px`;
export const spIconLabelGap = `${SP.iconLabelGap}px`;
export const spGridIconTextGap = `${SP.gridIconTextGap}px`;
export const spGridLabelValueGap = `${SP.gridLabelValueGap}px`;

/** MUI Stack spacing multipliers (theme factor 4px) */
export const spStack = {
  card: SP.card / 4,
  drawer: SP.drawer / 4,
  gridCellX: SP.gridCellX / 4,
  gridCellY: SP.gridCellY / 4,
};

export const spDrawerPadding = spStack.drawer;
export const spSectionGap = spStack.card;

export const spCardPaddingSx = { p: `${SP.card}px` };
export const spDrawerInsetSx = { px: `${SP.drawer}px`, pt: `${SP.drawer}px` };
export const spPanelShellSx = {
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
};
export const spGridCellPaddingSx = { px: `${SP.gridCellX}px`, py: `${SP.gridCellY}px` };
export const spSectionBodyTopSx = { pt: `${SP.gridCellY}px` };

export const spBlockCardContentPaddingSx = {
  py: `${SP.card}px`,
  pr: `${SP.card}px`,
  pl: `${SP.card + 4}px`,
};

export const spChipSx = {
  height: 26,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiChip-label": { px: `${SP.chipLabel}px`, py: 0, lineHeight: 1.2 },
  "& .MuiChip-icon": { ml: `${SP.chipLabel}px` },
};

export const spMonoFontFamily =
  "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const spSeverityChipLabelSx = {
  px: "8px",
  py: "2px",
  textTransform: "uppercase",
  lineHeight: 1.2,
};

export const spClassificationChipLabelSx = {
  px: "11px",
  py: 0,
  lineHeight: 1.2,
};

export const spClassificationChipSx = {
  height: 22,
  fontSize: 11,
  fontWeight: 500,
  fontFamily: spMonoFontFamily,
  color: colors.neutral[600],
  backgroundColor: colors.neutral[50],
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: "999px",
  "& .MuiChip-label": spClassificationChipLabelSx,
};

export function spSeverityChipSx(severity) {
  const palette = drawerSeverityPalette(severity);
  return {
    height: "auto",
    minHeight: 22,
    fontSize: 10,
    fontWeight: 600,
    fontFamily: spMonoFontFamily,
    borderRadius: "999px",
    backgroundColor: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.fg}`,
    "& .MuiChip-label": spSeverityChipLabelSx,
  };
}

export const spHeaderGradient = "linear-gradient(180deg, #FFFFFF 0%, #F8F9FF 100%)";

export const spDrawerLine = "rgba(55, 62, 120, 0.14)";

export const spDrawerShellSx = {
  background: `
    radial-gradient(680px 360px at 100% 0%, rgba(99, 113, 247, 0.07), transparent 64%),
    #FFFFFF
  `,
  borderLeft: "1px solid rgba(55, 62, 120, 0.26)",
  boxShadow: "-30px 0 70px rgba(28, 32, 70, 0.16)",
};

export const spHeroHeaderShellSx = {
  position: "relative",
  px: `${SP.drawer}px`,
  pt: "20px",
  pb: "22px",
  borderBottom: `1px solid ${spDrawerLine}`,
  background: spHeaderGradient,
  flexShrink: 0,
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "radial-gradient(420px 220px at 78% -30%, rgba(99,113,247,0.08), transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: "2px",
    background: "linear-gradient(90deg, transparent, #6371F7, transparent)",
    opacity: 0.55,
  },
};

/** Hero block inside scroll body — horizontal inset comes from spScrollBodySx */
export const spHeroContentSx = {
  pt: 0,
  pb: "22px",
  borderBottom: `1px solid ${spDrawerLine}`,
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  width: "100%",
};

export const spScrollBodySx = {
  px: `${SP.drawer}px`,
  pt: "18px",
  pb: "26px",
  gap: "14px",
};

export const spSectionHeadSx = {
  px: "16px",
  py: "14px",
};

export const spSectionBodySx = {
  px: "16px",
  pb: "16px",
  pt: 0,
  display: "flex",
  flexDirection: "column",
  gap: "13px",
};

export const spDrawerFooterSx = {
  px: "22px",
  py: "14px",
  borderTop: `1px solid ${spDrawerLine}`,
  flexShrink: 0,
  background: "linear-gradient(180deg, transparent, rgba(40, 46, 100, 0.02))",
};

export const spHeaderMarkSx = {
  width: 44,
  height: 44,
  flexShrink: 0,
  borderRadius: "13px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(150deg, #EEF1FF, #E2E6FB)",
  border: "1px solid rgba(99,113,247,0.32)",
  color: PRIMARY_COLOR,
  boxShadow: "0 6px 18px rgba(99,113,247,0.18)",
};

export const spIdChipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  height: 26,
  px: "9px",
  borderRadius: "7px",
  maxWidth: "100%",
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  border: `1px solid ${spDrawerLine}`,
  transition: "all 0.15s",
  "&:hover": {
    borderColor: "rgba(55, 62, 120, 0.26)",
  },
};

export const spIconBoxSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: "8px",
  backgroundColor: colors.primary[100],
  border: "1px solid #E5E7FF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const spHeaderIconBoxSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: "8px",
  backgroundColor: colors.primary[100],
  border: "1px solid #E5E7FF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const spGridIconBoxSx = {
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: "6px",
  backgroundColor: "#F3F4F6",
  border: `1px solid ${BORDER_COLOR}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const NEUTRAL_ICON_BOX_PALETTE = { bg: "#F3F4F6", border: BORDER_COLOR };

const ICON_BOX_PALETTE_BY_COLOR = {
  [PRIMARY_COLOR.toLowerCase()]: { bg: colors.primary[100], border: "#E5E7FF" },
  "#eef0fe": { bg: "#EEF0FE", border: "#E5E7FF" },
  [colors.green[300].toLowerCase()]: { bg: colors.green[10], border: "rgba(31,193,107,0.32)" },
  [colors.red[100].toLowerCase()]: { bg: colors.red[10], border: "rgba(251,55,72,0.28)" },
  [colors.red[200].toLowerCase()]: { bg: colors.red[10], border: "rgba(251,55,72,0.30)" },
  [colors.yellow[200].toLowerCase()]: { bg: colors.yellow[10], border: "rgba(223,180,0,0.45)" },
  [TEXT_PRIMARY.toLowerCase()]: NEUTRAL_ICON_BOX_PALETTE,
  [TEXT_SECONDARY.toLowerCase()]: NEUTRAL_ICON_BOX_PALETTE,
};

export function resolveIconBoxPalette(iconColor) {
  const key = String(iconColor || TEXT_PRIMARY).toLowerCase();
  if (ICON_BOX_PALETTE_BY_COLOR[key]) {
    return ICON_BOX_PALETTE_BY_COLOR[key];
  }

  for (const palette of Object.values(DRAWER_SEVERITY_PALETTE)) {
    if (String(palette.fg).toLowerCase() === key) {
      return { bg: palette.bg, border: palette.border };
    }
  }

  return NEUTRAL_ICON_BOX_PALETTE;
}

const ICON_BOX_SIZE_BASE = {
  grid: spGridIconBoxSx,
  section: spIconBoxSx,
  header: spHeaderIconBoxSx,
};

export function spIconBoxForColor(iconColor, baseSize = "grid") {
  const sizeSx = ICON_BOX_SIZE_BASE[baseSize] || spGridIconBoxSx;
  const { bg, border } = resolveIconBoxPalette(iconColor);
  return {
    ...sizeSx,
    backgroundColor: bg,
    border: `1px solid ${border}`,
  };
}

export const spMethodChipSx = {
  ...spChipSx,
  height: 24,
  backgroundColor: colors.primary[100],
  color: PRIMARY_COLOR,
  border: "1px solid #E5E7FF",
  flexShrink: 0,
};

/** Sticky drawer header — method/path row path segment (single-line ellipsis) */
export const spHeaderPathSx = {
  fontSize: 12.5,
  lineHeight: spLh.snug,
  color: TEXT_PRIMARY,
  fontFamily: spMonoFontFamily,
  minWidth: 0,
  flex: 1,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

/** Sticky drawer header — event/id chip value (single-line ellipsis) */
export const spHeaderIdValueSx = {
  fontSize: 11,
  lineHeight: spLh.tight,
  color: TEXT_SECONDARY,
  fontFamily: spMonoFontFamily,
  minWidth: 0,
  flex: 1,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

export const spIdPillSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 1.5,
  py: 0.75,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: colors.neutral[50],
  maxWidth: "100%",
};

export const spBlockCardBorder = "rgba(251, 55, 72, 0.28)";

export const spBlockedBannerSx = {
  flex: 1,
  minWidth: 0,
  position: "relative",
  display: "flex",
  alignItems: "center",
  borderRadius: spPanelRadius,
  border: "1px solid rgba(251, 55, 72, 0.28)",
  overflow: "hidden",
  background: "linear-gradient(135deg, rgba(251,55,72,0.10), rgba(251,55,72,0.03))",
  p: "15px 16px",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    zIndex: 0,
    backgroundColor: colors.red[100],
    boxShadow: "0 0 14px rgba(251,55,72,0.5)",
  },
};

export const spBlockIconBoxSx = {
  width: 46,
  height: 46,
  flexShrink: 0,
  borderRadius: "12px",
  backgroundColor: "rgba(251,55,72,0.12)",
  border: "1px solid rgba(251,55,72,0.32)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  zIndex: 1,
};

export const spBurstCalloutSx = {
  ...spCardPaddingSx,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spIconLabelGap,
  borderRadius: spPanelRadius,
  border: `1px solid ${spBlockCardBorder}`,
  backgroundColor: colors.red[10],
};

export const spBurstIconBoxSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: "8px",
  backgroundColor: colors.red[10],
  border: `1px solid ${spBlockCardBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const spSimilarFilterCardSx = {
  ...spGridCellPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: "#fff",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: colors.neutral[50],
  },
};

export const spSimilarFilterLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  lineHeight: spLh.px16,
  color: colors.neutral[500],
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

export const spSimilarFilterValueSx = {
  fontSize: 12.5,
  fontWeight: 600,
  lineHeight: spLh.px16,
  color: TEXT_PRIMARY,
  wordBreak: "break-all",
};

export const spRawJsonViewerShellSx = {
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  overflow: "hidden",
};

export const spRawJsonViewerHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: `${SP.gridCellX}px`,
  px: `${SP.card}px`,
  py: "10px",
  backgroundColor: colors.neutral[50],
  borderBottom: `1px solid ${BORDER_COLOR}`,
};

export const spRawJsonViewerBodySx = {
  backgroundColor: "#F8F9FC",
  p: `${SP.card}px`,
  maxHeight: 400,
  overflow: "auto",
};

export const spRawJsonViewerFilenameSx = {
  fontFamily: spMonoFontFamily,
  fontSize: 12,
  lineHeight: spLh.px18,
  color: TEXT_SECONDARY,
};

export const spJsonHighlightStyle = {
  hljs: {
    display: "block",
    overflowX: "auto",
    padding: 0,
    background: "transparent",
    color: TEXT_PRIMARY,
  },
  "hljs-attr": { color: PRIMARY_COLOR },
  "hljs-string": { color: "#22863A" },
  "hljs-number": { color: "#D97706" },
  "hljs-literal": { color: "#D97706" },
  "hljs-punctuation": { color: PRIMARY_COLOR },
};

export const spCriticalBadgeSx = {
  height: "auto",
  borderRadius: "999px",
  backgroundColor: colors.red[100],
  color: "#fff",
  border: "none",
  boxShadow: "0 2px 8px rgba(251, 55, 72, 0.35)",
  "& .MuiChip-label": {
    px: 1,
    py: "3px",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: spLh.tight,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
};

export const spConfidenceCardSx = {
  flexShrink: 0,
  width: 118,
  borderRadius: spPanelRadius,
  border: "1px solid rgba(31, 193, 107, 0.28)",
  background: "linear-gradient(135deg, rgba(31,193,107,0.10), rgba(31,193,107,0.02))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  py: "11px",
  px: 0,
};

export const spKillChainPanelSx = {
  p: "15px 16px 14px",
  borderRadius: spPanelRadius,
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  border: `1px solid ${spDrawerLine}`,
};

export function SmartParseSectionDivider() {
  return (
    <Box
      sx={{
        mx: `-${SP.drawer}px`,
        height: 36,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", height: "1px", backgroundColor: BORDER_COLOR }} />
    </Box>
  );
}

export const spNumberedRowSx = {
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: "#FAFBFC",
};

export const spNumberedBadgeRowShellSx = {
  ...spNumberedRowSx,
  px: "13px",
  py: "11px",
};

export const spObjectiveCardSx = {
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid rgba(223, 180, 0, 0.35)`,
  backgroundColor: colors.yellow[10],
};

export const spNetworkFieldRowSx = {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
  px: "10px",
  py: "8px",
  borderRadius: "6px",
  border: "1px solid rgba(55, 62, 120, 0.08)",
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  transition: "background 0.15s",
  "&:hover": {
    backgroundColor: "rgba(40, 46, 100, 0.055)",
  },
};

export const spNetworkFieldLabelSx = {
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1.2,
  color: "#8A8FA8",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0,
};

export const spNetworkFieldValueSx = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.3,
  color: TEXT_PRIMARY,
  wordBreak: "break-all",
};

export const spNetworkDetailCardTitleSx = {
  fontSize: 10,
  fontWeight: 600,
  lineHeight: spLh.px16,
  color: colors.neutral[500],
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

/** Abuse score card header — reference `.rep-lbl`, slightly larger for scanability */
export const spAbuseScoreTitleSx = {
  fontSize: 12,
  fontWeight: 700,
  lineHeight: spLh.tight,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#8A8FA8",
};

export const spAbuseScoreCardSx = {
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: "#fff",
};

export const spRequestHeadersCardSx = {
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: colors.neutral[50],
};

export const spRequestHeaderLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  lineHeight: spLh.px16,
  color: colors.neutral[500],
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: "3px",
};

export const spRequestHeaderValueSx = {
  fontSize: 11,
  fontWeight: 400,
  lineHeight: spLh.relaxed,
  color: PRIMARY_COLOR,
  wordBreak: "break-all",
};

export const spBodySx = {
  fontSize: 13,
  lineHeight: spLh.body,
  color: TEXT_PRIMARY,
};

export const spSecondaryBodySx = {
  fontSize: 12.5,
  lineHeight: spLh.relaxed,
  color: TEXT_SECONDARY,
};

export const spMetaSx = {
  fontSize: 12.5,
  lineHeight: spLh.relaxed,
  color: TEXT_SECONDARY,
};

export const spSectionLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  color: "#8A8FA8",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  lineHeight: spLh.tight,
  mb: 0,
};

export const spHeadlineSx = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: spLh.px25,
  color: TEXT_PRIMARY,
};

/** Detection Summary — matches WAF drawer reference `.sum-*` */
export const spSumHeadlineSx = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: spLh.snug,
  letterSpacing: "-0.01em",
  color: TEXT_PRIMARY,
};

export const spSumBodySx = {
  fontSize: 13,
  lineHeight: spLh.body,
  color: "#565B76",
  "& strong": {
    color: TEXT_PRIMARY,
    fontWeight: 600,
  },
};

export const spSumFlagRowSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};

export const spSumFlagBaseSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontSize: 11,
  fontWeight: 600,
  padding: "3px 10px",
  borderRadius: "999px",
  lineHeight: spLh.snug,
};

export const spSumFlagBlockSx = {
  ...spSumFlagBaseSx,
  backgroundColor: colors.red[10],
  color: colors.red[200],
  border: "1px solid rgba(251, 55, 72, 0.30)",
};

export const spSumFlagStageSx = {
  ...spSumFlagBaseSx,
  backgroundColor: "rgba(40, 46, 100, 0.055)",
  color: "#565B76",
  border: "1px solid rgba(55, 62, 120, 0.14)",
};

export const spObjectiveCalloutSx = {
  display: "flex",
  gap: "12px",
  padding: "13px 14px",
  borderRadius: "8px",
  background: "linear-gradient(135deg, rgba(242, 180, 41, 0.16), rgba(242, 180, 41, 0.04))",
  border: "1px solid rgba(242, 180, 41, 0.34)",
};

export const spObjectiveIconSx = {
  width: 30,
  height: 30,
  borderRadius: "9px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(242, 180, 41, 0.18)",
  border: "1px solid rgba(242, 180, 41, 0.42)",
  color: "#B07D11",
};

export const spObjectiveLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#B07D11",
  mb: "4px",
  lineHeight: spLh.tight,
};

export const spObjectiveValueSx = {
  fontSize: 12.5,
  lineHeight: spLh.relaxed,
  color: "#565B76",
};

export const spWhyOverlineSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: "#8A8FA8",
  mb: "8px",
  lineHeight: spLh.tight,
};

export const spWhyItemSx = {
  display: "flex",
  gap: "11px",
  alignItems: "flex-start",
  padding: "11px 13px",
  borderRadius: "8px",
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  border: "1px solid rgba(55, 62, 120, 0.08)",
};

export const spWhyBadgeSx = {
  width: 22,
  height: 22,
  borderRadius: "7px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  fontFamily: spMonoFontFamily,
  backgroundColor: "rgba(99, 113, 247, 0.12)",
  border: "1px solid rgba(99, 113, 247, 0.30)",
  color: "#4E5AD6",
};

export const spWhyTextSx = {
  fontSize: 12.5,
  lineHeight: spLh.relaxed,
  color: "#565B76",
  pt: "2px",
  flex: 1,
  minWidth: 0,
};

export const spRemItemSx = {
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  padding: "11px 13px",
  borderRadius: "8px",
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  border: "1px solid rgba(55, 62, 120, 0.08)",
  transition: "background 0.15s, border-color 0.15s",
  "&:hover": {
    backgroundColor: "rgba(99, 113, 247, 0.06)",
    borderColor: "rgba(99, 113, 247, 0.28)",
  },
};

export const spRemBadgeSx = {
  ...spWhyBadgeSx,
  width: 24,
  height: 24,
  backgroundColor: "rgba(99, 113, 247, 0.10)",
  border: "1px solid rgba(99, 113, 247, 0.26)",
};

export const spGridCellSx = {
  ...spNetworkFieldRowSx,
};

/** Assessment grid cells — matches WAF drawer reference `.assess-cell` */
export const spAssessCellSx = {
  display: "flex",
  alignItems: "center",
  gap: spGridIconTextGap,
  px: "10px",
  py: "8px",
  borderRadius: "8px",
  border: "1px solid rgba(55, 62, 120, 0.08)",
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  minWidth: 0,
};

export const spAssessIconBoxSx = {
  ...spGridIconBoxSx,
  borderRadius: "8px",
  backgroundColor: "#fff",
};

export const spAssessLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  lineHeight: spLh.tight,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#8A8FA8",
  mb: "3px",
  whiteSpace: "nowrap",
};

export const spAssessValueSx = {
  fontSize: 12.5,
  fontWeight: 500,
  lineHeight: spLh.normal,
  color: TEXT_PRIMARY,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Reference `.assess-cell .ac-v.mono` — paths, IDs, technical strings */
export const spAssessValueMonoSx = {
  ...spAssessValueSx,
  fontFamily: spMonoFontFamily,
  fontSize: 11.5,
};

export function spAssessIconBoxForColor(iconColor) {
  const { bg, border } = resolveIconBoxPalette(iconColor);
  const isNeutral = bg === NEUTRAL_ICON_BOX_PALETTE.bg;
  return {
    ...spAssessIconBoxSx,
    backgroundColor: isNeutral ? "#fff" : bg,
    border: `1px solid ${border}`,
  };
}

/** Assessment / detection-summary grid cell with leading icon (Smart Parse + Signature WAF). */
export function AssessGridCell({
  label,
  value,
  icon: Icon,
  valueColor,
  monospace = false,
  copyable = false,
  onCopy = null,
  wrap = false,
  sx,
}) {
  const display = value != null && value !== "" ? String(value) : "—";
  const resolvedValueColor =
    valueColor || (display === "—" ? TEXT_SECONDARY : TEXT_PRIMARY);
  const iconColor = Icon ? resolvedValueColor : null;
  const valueSx = {
    ...(monospace && display !== "—" ? spAssessValueMonoSx : spAssessValueSx),
    color: resolvedValueColor,
    fontWeight: display === "—" ? 400 : 500,
    minWidth: 0,
    flex: 1,
    ...(wrap
      ? { whiteSpace: "normal", wordBreak: "break-all", overflow: "visible", textOverflow: "clip" }
      : {}),
  };

  const showCopy = copyable && onCopy && display !== "—";

  return (
    <Box
      sx={{
        ...spAssessCellSx,
        alignItems: "flex-start",
        minHeight: 52,
        height: "100%",
        ...sx,
      }}
    >
      {Icon && (
        <Box
          sx={{
            ...spAssessIconBoxForColor(iconColor),
            ...(monospace || showCopy ? { mt: "2px" } : {}),
          }}
        >
          <Icon size={14} color={iconColor} aria-hidden />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={spAssessLabelSx}>{label}</Typography>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={{ gap: "6px" }}
        >
          <Typography title={display} sx={{ ...valueSx, flex: 1, minWidth: 0 }}>
            {display}
          </Typography>
          {showCopy && (
            <IconButton
              size="small"
              onClick={() => onCopy(value)}
              aria-label={`Copy ${label}`}
              sx={{
                width: 22,
                height: 22,
                borderRadius: "6px",
                flexShrink: 0,
                mt: "-1px",
                color: TEXT_SECONDARY,
                border: "1px solid transparent",
                "&:hover": {
                  backgroundColor: "rgba(40, 46, 100, 0.055)",
                  color: TEXT_PRIMARY,
                },
              }}
            >
              <LayerdIcon icon={DETECTION_ICONS.copy} size={14} color="neutral" />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

AssessGridCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.elementType,
  valueColor: PropTypes.string,
  monospace: PropTypes.bool,
  copyable: PropTypes.bool,
  onCopy: PropTypes.func,
  wrap: PropTypes.bool,
  sx: PropTypes.object,
};

/** Two-column assessment / summary grid shared across v3 event drawers. */
export function EventAssessGrid({ cells, onCopy = null }) {
  if (!cells || cells.length === 0) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: `${SP.assessGridGap}px`,
      }}
    >
      {cells.map((cell) => {
        const shellSx = {
          minHeight: 52,
          height: "100%",
          display: "flex",
          ...(cell.gridColumn ? { gridColumn: cell.gridColumn } : {}),
        };

        if (cell.variant === "status") {
          return (
            <Box key={cell.key} sx={{ ...spAssessCellSx, ...shellSx, alignItems: "flex-start" }}>
              <DrawerResponseStatusField label={cell.label} status={cell.value} embedded />
            </Box>
          );
        }

        return (
          <Box key={cell.key} sx={shellSx}>
            <AssessGridCell
              label={cell.label}
              value={cell.value}
              icon={cell.icon}
              valueColor={cell.valueColor}
              monospace={cell.monospace}
              copyable={cell.copyable}
              onCopy={cell.onCopy || onCopy}
              wrap={cell.wrap}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

EventAssessGrid.propTypes = {
  cells: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.elementType,
    valueColor: PropTypes.string,
    monospace: PropTypes.bool,
    copyable: PropTypes.bool,
    onCopy: PropTypes.func,
    wrap: PropTypes.bool,
    variant: PropTypes.string,
    gridColumn: PropTypes.string,
  })),
  onCopy: PropTypes.func,
};

export const spCardSx = {
  flex: 1,
  ...spCardPaddingSx,
  borderRadius: spPanelRadius,
  border: `1px solid ${BORDER_COLOR}`,
  backgroundColor: "#F3F4F6",
};

export const spNumberedItemTextSx = {
  fontSize: 12.5,
  lineHeight: spLh.px22,
  color: "#565B76",
};

export function NumberedBadgeRow({ index, text, variant = "why" }) {
  const isRem = variant === "rem";
  const shellSx = isRem ? spRemItemSx : spWhyItemSx;
  const badgeSx = isRem ? spRemBadgeSx : spWhyBadgeSx;
  const textSx = isRem ? { ...spWhyTextSx, pt: "3px" } : spWhyTextSx;

  return (
    <Box sx={shellSx}>
      <Box component="span" sx={badgeSx}>
        {index + 1}
      </Box>
      <Typography component="div" sx={textSx}>
        {text}
      </Typography>
    </Box>
  );
}

NumberedBadgeRow.propTypes = {
  index: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["why", "rem"]),
};

export function DrawerNumberedList({ items, variant = "rem", subtitle }) {
  const list = (items || []).filter((item) => item != null && String(item).trim() !== "");
  if (list.length === 0) return null;

  return (
    <Stack sx={{ gap: "13px" }}>
      {subtitle ? (
        <Typography sx={spSecondaryBodySx}>{subtitle}</Typography>
      ) : null}
      <Stack sx={{ gap: "6px" }}>
        {list.map((item, i) => (
          <NumberedBadgeRow
            key={i}
            index={i}
            text={typeof item === "string" ? item : String(item)}
            variant={variant}
          />
        ))}
      </Stack>
    </Stack>
  );
}

DrawerNumberedList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  variant: PropTypes.oneOf(["why", "rem"]),
  subtitle: PropTypes.string,
};

/** @deprecated use NumberedBadgeRow */
export function NumberedItemRow({ index, text }) {
  return <NumberedBadgeRow index={index} text={text} />;
}

export function normalizeDrawerFieldValue(value) {
  const raw = value != null && value !== "" ? String(value).trim() : "";
  return raw && raw !== "(Empty)" ? raw : "—";
}

export function isDrawerValuePresent(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text.length > 0 && text !== "—" && text !== "(Empty)";
}

/** Coerce raw event values into drawer field values (null when absent). */
export function toDrawerFieldValue(value) {
  return isDrawerValuePresent(value) ? String(value).trim() : null;
}

export function drawerFieldsHaveContent(fields) {
  return (fields || []).some((field) => {
    if (!field) return false;
    if (field.variant === "status") {
      return field.value !== null && field.value !== undefined && String(field.value).trim() !== "";
    }
    return isDrawerValuePresent(field.value);
  });
}

export function hasAbuseReputationData(event) {
  if (!event || typeof event !== "object") return false;

  const abuseip = event.abuseip;
  if (abuseip && typeof abuseip === "object") {
    if (hasIpReputationEnrichment(abuseip)) return true;
  }

  const score = event.abuse_score ?? event.ip_reputation_score;
  return score !== undefined && score !== null && Number(score) > 0;
}

/** AbuseIPDB / ip_reputation enrichment — camelCase or snake_case keys. */
export function hasIpReputationEnrichment(reputation) {
  if (!reputation || typeof reputation !== "object") return false;
  if (isDrawerValuePresent(reputation.isp)) return true;
  if (isDrawerValuePresent(reputation.usageType)) return true;
  if (isDrawerValuePresent(reputation.usage_type)) return true;
  if (isDrawerValuePresent(reputation.countryCode)) return true;
  if (isDrawerValuePresent(reputation.country_code)) return true;
  const score = reputation.abuseConfidenceScore ?? reputation.abuse_confidence_score;
  if (score !== undefined && score !== null && Number(score) > 0) return true;
  const reports = reputation.totalReports ?? reputation.total_reports;
  return typeof reports === "number" && reports > 0;
}

export function NetworkFieldRow({
  label,
  value,
  copyable = false,
  onCopy = null,
  monospace = false,
  valueColor,
  truncate = false,
}) {
  const display = normalizeDrawerFieldValue(value);

  return (
    <Box sx={{ ...spNetworkFieldRowSx, minWidth: truncate ? 0 : undefined }}>
      <Typography sx={spNetworkFieldLabelSx}>
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Typography
          sx={{
            ...spNetworkFieldValueSx,
            color: valueColor || TEXT_PRIMARY,
            flex: 1,
            minWidth: 0,
            ...(monospace ? { fontFamily: spMonoFontFamily, fontSize: 11 } : {}),
            ...(truncate
              ? {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "normal",
              }
              : {}),
          }}
        >
          {display}
        </Typography>
        {copyable && onCopy && display !== "—" && (
          <IconButton
            size="small"
            onClick={() => onCopy(value)}
            aria-label={`Copy ${label}`}
            sx={{
              width: 22,
              height: 22,
              borderRadius: "6px",
              flexShrink: 0,
              color: TEXT_SECONDARY,
              border: "1px solid transparent",
              "&:hover": {
                backgroundColor: "rgba(40, 46, 100, 0.055)",
                color: TEXT_PRIMARY,
              },
            }}
          >
            <LayerdIcon icon={DETECTION_ICONS.copy} size={14} color="neutral" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

NetworkFieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  copyable: PropTypes.bool,
  onCopy: PropTypes.func,
  monospace: PropTypes.bool,
  valueColor: PropTypes.string,
  truncate: PropTypes.bool,
};

export function DrawerResponseStatusField({ label = "Status Code", status, embedded = false }) {
  const raw = status != null && status !== "" ? status : null;
  const statusNum = raw != null ? Number(raw) : null;
  const isErrorStatus = statusNum != null && !Number.isNaN(statusNum) && statusNum >= 400;

  const content = raw != null ? (
    <Chip
      size="small"
      label={String(raw)}
      sx={{
        height: 24,
        width: "fit-content",
        fontSize: 12,
        fontWeight: 600,
        borderRadius: "6px",
        backgroundColor: isErrorStatus ? colors.red[10] : colors.green[10],
        color: isErrorStatus ? colors.red[200] : colors.green[300],
        border: `1px solid ${isErrorStatus ? "rgba(251,55,72,0.30)" : "rgba(31,193,107,0.32)"}`,
        "& .MuiChip-label": { px: "10px", py: 0, lineHeight: 1.2 },
      }}
    />
  ) : (
    <Typography sx={spNetworkFieldValueSx}>—</Typography>
  );

  if (embedded) {
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={spAssessLabelSx}>{label}</Typography>
        {content}
      </Box>
    );
  }

  return (
    <Box sx={spNetworkFieldRowSx}>
      <Typography sx={spNetworkFieldLabelSx}>{label}</Typography>
      {content}
    </Box>
  );
}

DrawerResponseStatusField.propTypes = {
  label: PropTypes.string,
  status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  embedded: PropTypes.bool,
};

function renderDrawerFieldCell(
  { key, label, value, copyable, monospace, valueColor, variant, content },
  { truncate, onCopy }
) {
  if (variant === "status") {
    return (
      <DrawerResponseStatusField
        key={key || label}
        label={label}
        status={value}
      />
    );
  }

  if (content) {
    return (
      <Box key={key || label} sx={spNetworkFieldRowSx}>
        <Typography sx={spNetworkFieldLabelSx}>{label}</Typography>
        {content}
      </Box>
    );
  }

  return (
    <NetworkFieldRow
      key={key || label}
      label={label}
      value={value}
      copyable={copyable}
      onCopy={onCopy}
      monospace={monospace}
      valueColor={valueColor}
      truncate={truncate}
    />
  );
}

export function DrawerFieldStack({ fields, onCopy, spacing = 1.5, columns = 1 }) {
  const rows = (fields || []).filter(Boolean);
  if (rows.length === 0) return null;

  const truncate = columns === 2;
  const cellOptions = { truncate, onCopy };

  if (columns === 2) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: `${SP.assessGridGap}px`,
        }}
      >
        {rows.map((field) => (
          <Box
            key={field.key || field.label}
            sx={{
              minWidth: 0,
              ...(field.fullRow ? { gridColumn: "1 / -1" } : {}),
            }}
          >
            {renderDrawerFieldCell(field, cellOptions)}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Stack spacing={spacing}>
      {rows.map((field) => renderDrawerFieldCell(field, cellOptions))}
    </Stack>
  );
}

DrawerFieldStack.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      copyable: PropTypes.bool,
      monospace: PropTypes.bool,
      valueColor: PropTypes.string,
      variant: PropTypes.oneOf(["status"]),
      content: PropTypes.node,
      fullRow: PropTypes.bool,
    })
  ),
  onCopy: PropTypes.func,
  spacing: PropTypes.number,
  columns: PropTypes.oneOf([1, 2]),
};

export function NetworkCountryFlagRow({
  countryCode,
  country,
  sourceIp,
  requireSourceIp = false,
}) {
  const flagCode = countryCode
    || (country && String(country).length === 2 ? String(country) : null);

  if (!flagCode) return null;
  if (requireSourceIp && !sourceIp) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <ReactCountryFlag
        countryCode={flagCode}
        svg
        style={{
          width: "32px",
          height: "22px",
          borderRadius: "4px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
      />
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
        {toDrawerFieldValue(country) !== "—" ? toDrawerFieldValue(country) : flagCode}
      </Typography>
    </Stack>
  );
}

NetworkCountryFlagRow.propTypes = {
  countryCode: PropTypes.string,
  country: PropTypes.string,
  sourceIp: PropTypes.string,
  requireSourceIp: PropTypes.bool,
};

export function NetworkInformationPanel({ network, onCopy, showCountryFlag = true }) {
  if (!network) return null;

  const {
    sourceIp,
    sourcePort,
    ja3Fingerprint,
    country,
    countryCode,
    asn,
    city,
    region,
    timezone,
  } = network;

  if (!sourceIp && !country && !ja3Fingerprint) return null;

  const flagCode = countryCode || (country && String(country).length === 2 ? String(country) : null);
  const asnLabel = asn && asn !== "0" ? (String(asn).startsWith("AS") ? asn : `AS${asn}`) : null;

  return (
    <Stack spacing={2}>
      {showCountryFlag && flagCode && (
        <NetworkCountryFlagRow countryCode={flagCode} country={country} />
      )}
      <DrawerFieldStack
        columns={2}
        onCopy={onCopy}
        fields={[
          { key: "sourceIp", label: "Source IP", value: sourceIp, copyable: !!sourceIp, monospace: true },
          { key: "sourcePort", label: "Source Port", value: sourcePort },
          {
            key: "ja3",
            label: "JA3 Fingerprint",
            value: ja3Fingerprint,
            copyable: !!ja3Fingerprint,
            monospace: true,
          },
          { key: "country", label: "Country", value: country },
          asnLabel ? { key: "asn", label: "ASN", value: asnLabel } : null,
          { key: "city", label: "City", value: city },
          { key: "region", label: "Region", value: region },
          { key: "timezone", label: "Timezone", value: timezone },
        ]}
      />
    </Stack>
  );
}

NetworkInformationPanel.propTypes = {
  network: PropTypes.object,
  onCopy: PropTypes.func,
  showCountryFlag: PropTypes.bool,
};

export function IpReputationPanel({
  ipReputation,
  showAbuseScore = true,
  showReportsSummary = false,
  hideIpAddressField = false,
}) {
  if (!ipReputation) return null;

  const abuseScore = ipReputation.abuseConfidenceScore ?? 0;
  const scoreColor = abuseScore > 70 ? ERROR_COLOR : abuseScore > 40 ? "#92400E" : "#16A34A";
  const barColor = abuseScore > 70 ? ERROR_COLOR : abuseScore > 40 ? "#CA8A04" : "#16A34A";
  const totalReports = ipReputation.totalReports;

  return (
    <Stack spacing={2}>
      {showReportsSummary && totalReports !== undefined && (
        <Typography sx={spSecondaryBodySx}>
          IP reported {totalReports} types.
        </Typography>
      )}
      {showAbuseScore && (
        <Box>
          <Typography sx={{ fontSize: 14, lineHeight: "30px", color: TEXT_SECONDARY, mb: 1 }}>
            Abuse Confidence Score
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1, height: 8, backgroundColor: "#F3F4F6", borderRadius: "8px", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${abuseScore}%`,
                  height: "100%",
                  backgroundColor: barColor,
                  borderRadius: "8px",
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 14, lineHeight: "30px", fontWeight: 600, color: scoreColor, minWidth: 50 }}>
              {abuseScore}%
            </Typography>
          </Stack>
        </Box>
      )}
      <DrawerFieldStack
        columns={2}
        fields={[
          !hideIpAddressField && ipReputation.ipAddress
            ? { key: "ipAddress", label: "IP Address", value: ipReputation.ipAddress, monospace: true }
            : null,
          { key: "isp", label: "ISP", value: ipReputation.isp },
          { key: "usageType", label: "Usage Type", value: ipReputation.usageType },
          { key: "countryCode", label: "Country Code", value: ipReputation.countryCode },
          {
            key: "totalReports",
            label: "Total Reports",
            value: ipReputation.totalReports !== undefined ? String(ipReputation.totalReports) : null,
          },
        ]}
      />
    </Stack>
  );
}

IpReputationPanel.propTypes = {
  ipReputation: PropTypes.object,
  showAbuseScore: PropTypes.bool,
  showReportsSummary: PropTypes.bool,
  hideIpAddressField: PropTypes.bool,
};

export function DrawerHeadersPanel({ title, headers, onCopy }) {
  const entries = headers
    ? Array.isArray(headers)
      ? headers
      : Object.entries(headers).map(([key, value]) => ({ key, label: key, value }))
    : [];

  if (entries.length === 0) return null;

  return (
    <Box sx={spRequestHeadersCardSx}>
      <Stack spacing={1.5}>
        <Typography sx={spNetworkDetailCardTitleSx}>{title}</Typography>
        {entries.map(({ key, label, value }) => (
          <NetworkFieldRow
            key={key || label}
            label={label}
            value={value}
            onCopy={onCopy}
            monospace
          />
        ))}
      </Stack>
    </Box>
  );
}

DrawerHeadersPanel.propTypes = {
  title: PropTypes.string.isRequired,
  headers: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  ]),
  onCopy: PropTypes.func,
};

export function SmartParseSectionCard({
  icon,
  title,
  count,
  headerExtra,
  children,
  collapsible = true,
  defaultExpanded = true,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Icon = icon;

  return (
    <Box
      sx={{
        borderRadius: spPanelRadius,
        backgroundColor: "#fff",
        border: `1px solid ${spDrawerLine}`,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": {
          borderColor: "rgba(55, 62, 120, 0.26)",
          boxShadow: "0 2px 10px rgba(28, 32, 70, 0.05)",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          ...spSectionHeadSx,
          cursor: collapsible ? "pointer" : "default",
          transition: "background 0.15s",
          "&:hover": collapsible ? { backgroundColor: "rgba(40, 46, 100, 0.025)" } : {},
        }}
        onClick={() => collapsible && setExpanded(!expanded)}
      >
        <Stack direction="row" alignItems="center" spacing={0} sx={{ gap: "11px" }}>
          {Icon && (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "9px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(99,113,247,0.10)",
                border: "1px solid rgba(99,113,247,0.26)",
                color: "#4E5AD6",
              }}
            >
              {typeof Icon === "function" ? (
                <LayerdIcon icon={Icon} size={16} color="primary" />
              ) : (
                <Icon style={{ width: 16, height: 16, color: PRIMARY_COLOR }} />
              )}
            </Box>
          )}
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, lineHeight: spLh.snug, color: TEXT_PRIMARY, letterSpacing: "-0.005em" }}>
            {title}
          </Typography>
          {headerExtra ? (
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              {headerExtra}
            </Box>
          ) : null}
          {count != null && Number.isFinite(Number(count)) && (
            <Box
              sx={{
                fontFamily: spMonoFontFamily,
                fontSize: 11,
                fontWeight: 600,
                color: "#4E5AD6",
                backgroundColor: "rgba(99,113,247,0.10)",
                border: "1px solid rgba(99,113,247,0.26)",
                px: 1,
                py: "1px",
                borderRadius: "999px",
                lineHeight: spLh.px18,
              }}
            >
              {count}
            </Box>
          )}
        </Stack>
        {collapsible && (
          <ChevronDown
            size={18}
            color={TEXT_SECONDARY}
            style={{
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.26s cubic-bezier(0.22, 0.61, 0.36, 1)",
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
      {expanded && (
        <Box sx={spSectionBodySx}>
          {children}
        </Box>
      )}
    </Box>
  );
}

SmartParseSectionCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  headerExtra: PropTypes.node,
  children: PropTypes.node,
  collapsible: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
};
